// Order Status Enum
export enum OrderStatus {
  PLACED = 'placed',
  CONFIRMED = 'confirmed',
  PREPARING = 'preparing',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  CANCELLED = 'cancelled',
  READY_FOR_PICKUP = 'ready_for_pickup'
}

export interface OrderI {
    id: number;
    restaurantName: string;
    restaurantAddress: string;
    time: string;
    items: {
        name: string;
        quantity: number;
        price: number;
        image: string;
    };
}

// Kitchen Registration Types
export interface KitchenRegistrationStep1 {
    businessName: string;
    ownerName: string;
    businessType: 'CLOUD_KITCHEN' | 'RESTAURANT';
    email: string;
    brandLogo?: string;
    alternateContact?: string;
}

export interface KitchenRegistrationStatus {
    isRegistered: boolean;
    currentStep?: number;
    completedSteps?: number[];
}

export interface KitchenProfile {
    id: string;
    businessName: string;
    ownerName: string;
    businessType: 'CLOUD_KITCHEN' | 'RESTAURANT';
    email: string;
    brandLogo?: string;
    alternateContact?: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    createdAt: string;
    updatedAt: string;
}

// Type definitions
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface UserData {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface RestaurantParams {
  page?: number;
  limit?: number;
  category?: string;
  sortBy?: string;
}

export interface OrderData {
  restaurantId: string;
  items: Array<{
    foodItemId: string;
    quantity: number;
  }>;
  deliveryAddress: string;
  paymentMethod: string;
}

export interface OrderParams {
  page?: number;
  limit?: number;
  status?: string;
}

export interface CartData {
  foodItemId: string;
  quantity: number;
  restaurantId: string;
}

export interface PaymentData {
  orderId: string;
  amount: number;
  method: string;
}

export interface PaymentMethod {
  type: string;
  details: any;
}

export interface DeliveryData {
  restaurantId: string;
  deliveryAddress: string;
}

export interface ReviewData {
  restaurantId: string;
  rating: number;
  comment: string;
}

// Category Types
export interface Category {
  _id: string;
  name: string;
  image?: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface CategoryResponse {
  status: boolean;
  message: string;
  data: Category[];
}

//----- dish response interfaces start ------

export interface DishResponseI {
  _id: string;
  restaurantId: string;
  dishName: string;
  categoryId: string;
  subCategoryId: string;
  description: string;
  shortDescription: string;
  images: DishImage[];

  foodTypeId: string;
  cuisineTypeId: string;
  mealTypeIds: string[];
  spiceLevelId: string;
  tagIds: string[];
  allergenIds: string[];
  dietaryLabelIds: string[];

  pricingModel: string;
  basePrice: number;
  comparePrice: number;
  variants: Variant[];

  gstRate: number;
  gstInclusive: boolean;
  packagingCharge: number;

  isAvailable: boolean;
  preparationTime: number;
  maxQuantityPerOrder: number;
  rating?: number;
  salesCount?: number;
  reviewCount?: number;

  serves: string;
  calories: number;
  isRecommended: boolean;
  sortOrder: number;

  availableForDelivery: boolean;
  availableForDineIn: boolean;
  availableOnQRMenu: boolean;

  status: string;
  createdAt: string;
  updatedAt: string;
  __v: number;

  // Populated Fields
  category: Category;
  subCategory: SubCategory;
  foodType: FoodType;
  cuisineType: CuisineType;
  mealTypes: MealType[];
  spiceLevel: SpiceLevel;
  tags: Tag[];
  allergens: Allergen[];
  dietaryLabels: DietaryLabel[];
}
export interface DishImage {
  url: string;
  isPrimary: boolean;
  sortOrder: number;
}

export interface Variant {
  // Empty for now — extend when needed
  [key: string]: any;
}

export interface Category {
  _id: string;
  name: string;
  description: string;
}

export interface SubCategory {
  _id: string;
  name: string;
  description: string;
}

export interface FoodType {
  _id: string;
  name: string;
}

export interface CuisineType {
  _id: string;
  name: string;
  description: string;
}

export interface MealType {
  _id: string;
  name: string;
}

export interface SpiceLevel {
  _id: string;
  name: string;
  level: number;
}

export interface Tag {
  _id: string;
  name: string;
}

export interface Allergen {
  _id: string;
  name: string;
}

export interface DietaryLabel {
  _id: string;
  name: string;
}

export interface Pagination {
  total: number;
  currentPage: number;
  totalPages: number;
}

// ---dish interface end------

// Restaurant Order API Interfaces
export interface GeoLocation {
  type: string;
  coordinates: [number, number];
}

export interface DeliveryAddress {
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  pincode: string;
  geoLocation: GeoLocation;
}

export interface OrderItem {
  _id: string;
  orderId: string;
  userId: string;
  restaurantId: string;
  dishId: string;
  variantId: string | null;
  addons: string[];
  dishName: string;
  variantName: string | null;
  addonNames: string[];
  price: number;
  addonPrice: number;
  totalItemPrice: number;
  quantity: number;
  __v: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrderRating {
  _id?: string;
  ratingType?: string;
  rating: number;
  review?: string;
  orderId?: string;
  userId?: string;
  restaurantId?: string;
  createdAt?: string;
}

export interface Order {
  _id: string;
  userId: string;
  restaurantId: string;
  subtotal: number;
  taxAmount: number;
  deliveryFee: number;
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: 'pending' | 'completed' | 'failed';
  orderStatus: 'placed' | 'confirmed' | 'preparing' | 'ready' | 'ready_for_pickup' | 'out_for_delivery' | 'delivered' | 'cancelled';
  deliveryAddress: DeliveryAddress;
  outForDeliveryOtp?: string | number;
  createdAt: string;
  updatedAt: string;
  deliveredAt?: string;
  cancellationReason?: string;
  user: Record<string, any>;
  items: OrderItem[];
  rating?: OrderRating[];
}

export interface OrdersPagination {
  currentPage: number;
  totalPages: number;
  total: number;
}

export interface OrdersResponse {
  status: boolean;
  data: Order[];
  pagination: OrdersPagination;
  message: string;
}

// Dashboard API Types
export interface OrderStats {
  newOrders: number;
  pendingOrders: number;
  completedOrders: number;
  cancelledOrders: number;
}

export interface SalesItem {
  id: string;
  dishName: string;
  quantity: number;
  totalAmount: number;
  revenueContribution?: number;
}

export interface Sales {
  items: SalesItem[];
  totalSalesAmount: number;
}

export type AnalyticsFilterType = 'today' | 'week' | 'month' | 'custom';

export interface AnalyticsPeriod {
  filter: AnalyticsFilterType;
  startDate: string;
  endDate: string;
  granularity: string;
}

export interface AnalyticsSummary {
  totalOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  grossSales: number;
  restaurantEarnings: number;
  uniqueCustomers: number;
  averageOrderValue: number;
  totalItemsSold: number;
  completionRate: number;
  cancellationRate: number;
}

export interface OrderStatusBreakdown {
  status: string;
  count: number;
}

export interface PaymentMethodBreakdown {
  method: string;
  count: number;
  amount: number;
}

export interface SalesTrendPoint {
  date?: string;
  label?: string;
  orders?: number;
  sales?: number;
  grossSales?: number;
  amount?: number;
}

export interface TopDish {
  dishId?: string;
  dishName?: string;
  name?: string;
  quantitySold?: number;
  quantity?: number;
  revenue?: number;
  totalRevenue?: number;
  revenueContribution?: number;
  percentage?: number;
  grossSales?: number;
}

export interface RestaurantAnalyticsData {
  period: AnalyticsPeriod;
  summary: AnalyticsSummary;
  orderStatus: OrderStatusBreakdown[];
  paymentMethods: PaymentMethodBreakdown[];
  salesTrend: SalesTrendPoint[];
  topDishes: TopDish[];
  generatedAt: string;
}

export interface DashboardData {
  filter: 'today' | 'week' | 'month';
  orderStats: OrderStats;
  sales: Sales;
  analytics?: RestaurantAnalyticsData;
}

export type DiscountType = 'percentage' | 'flat';
export type ApplicableOn = 'all' | 'specific';

export interface CouponData {
  _id: string;
  couponCode: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountCap?: number;
  minOrderValue: number;
  applicableOn: ApplicableOn;
  startDate: string;
  endDate: string;
  usageLimitGlobal: number;
  usageLimitPerUser: number;
  isActive: boolean;
  usageCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCouponRequest {
  couponCode: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountCap?: number;
  minOrderValue: number;
  applicableOn: ApplicableOn;
  startDate: string;
  endDate: string;
  usageLimitGlobal: number;
  usageLimitPerUser: number;
}

export interface WalletBalance {
  restaurantId: string;
  balance: number;
  totalEarned: number;
  totalPaidOut: number;
}

export type TransactionType = 'credit' | 'debit';

export interface WalletTransaction {
  _id: string;
  type: TransactionType;
  amount: number;
  balanceAfter: number;
  description: string;
  orderId: string | null;
  payoutRequestId: string | null;
  createdAt: string;
}

export type PayoutStatusType = 'pending' | 'approved' | 'rejected';

export interface BankDetails {
  accountName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
}

export interface PayoutRequest {
  _id: string;
  restaurantId: string;
  requestedAmount: number;
  status: PayoutStatusType;
  bankDetails: BankDetails;
  adminNote?: string;
  processedBy?: string;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePayoutRequest {
  requestedAmount: number;
  bankDetails: BankDetails;
}

export type StaticPageSlug =
  | 'kitchen-partner-terms-conditions'
  | 'kitchen-partner-privacy-policy';

export interface StaticPageData {
  _id: string;
  type: string;
  slug: string;
  title: string;
  content: string;
  banner?: string;
  seoTitle?: string;
  seoDescription?: string;
  updatedAt?: string;
}

export interface DashboardResponse {
  status: boolean;
  message: string;
  data: DashboardData;
}

// Single Order Detail API Interfaces
export interface OrderDetailUser {
  _id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  image: string;
}

export interface DeliveryAgent {
  _id: string;
  fullName: string;
  phoneNumber: string;
  image: string;
}

export interface OrderDetailItem {
  _id: string;
  dishId: string;
  variantId: string | null;
  addons: string[];
  dishName: string;
  variantName: string | null;
  addonNames: string[];
  price: number;
  addonPrice: number;
  totalItemPrice: number;
  quantity: number;
}

export interface OrderDetail {
  _id: string;
  userId: string;
  restaurantId: string;
  subtotal: number;
  taxAmount: number;
  deliveryFee: number;
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: 'pending' | 'completed' | 'failed';
  orderStatus: 'placed' | 'confirmed' | 'preparing' | 'out_for_delivery' | 'ready_for_pickup' | 'delivered' | 'cancelled';
  deliveryAddress: DeliveryAddress;
  outForDeliveryOtp?: number;
  createdAt: string;
  updatedAt: string;
  user: OrderDetailUser;
  deliveryAgent?: DeliveryAgent;
  items: OrderDetailItem[];
  cookingInstructions?: string;
}

export interface OrderDetailResponse {
  status: boolean;
  data: OrderDetail;
  message: string;
}

//----- kitchen notifications ------

export interface NotificationPayload {
  event: string;
  orderId?: string;
  userId?: string;
  restaurantId?: string;
  status?: string;
}

export interface NotificationItem {
  _id: string;
  recipientId: string;
  recipientType: string;
  event: string;
  title: string;
  message: string;
  priority: string;
  category: string;
  data: NotificationPayload;
  deepLink: string;
  groupKey: string;
  readAt: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPagination {
  total: number;
  currentPage: number;
  totalPages: number;
  unreadCount?: number;
}

export interface NotificationsListResponse {
  status: boolean;
  data: NotificationItem[];
  pagination?: NotificationPagination;
  message?: string;
}

export interface UnreadCountResponse {
  status: boolean;
  data: { unreadCount: number };
  message?: string;
}

export const isNotificationUnread = (notification: NotificationItem) =>
  notification.readAt === null;

export const getUnreadCountValue = (
  data?: { unreadCount?: number } | null
) => data?.unreadCount ?? 0;